import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { FaDownload } from "react-icons/fa";
import { FaFilePdf, FaExternalLinkAlt } from "react-icons/fa";
import { IoIosArrowBack } from "react-icons/io";

import { apiRequest } from "../api/apiRequest";

import Loading from "../components/Loading";
import Navbar from "../components/Navbar";
import SideMenu from "../components/SideMenu";

const styles = {
    cardProjetoLei: {
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        backgroundColor: '#fff',
    },
    cardTitle: {
        fontWeight: 'bold',
        color: '#043A5E',
        fontSize: '1.25rem',
        marginTop: 0,
        borderBottom: '2px solid #f0f0f0',
        paddingBottom: '10px',
        marginBottom: '15px',
    },
    infoLabel: {
        fontWeight: 'bold',
        color: '#555',
    },
    infoValue: {
        color: '#333',
    },
    paragraph: {
        marginBottom: '8px',
        fontSize: '14px',
    },
    ementaContainer: {
        marginTop: '15px',
        borderTop: '1px solid #eee',
        paddingTop: '15px',
    },
    ementaText: {
        textAlign: 'justify',
    },
    timelineItem: {
        marginBottom: '20px',
        position: 'relative',
        padding: '10px',
        borderRadius: '6px',
        backgroundColor: '#f9f9f9',
        borderLeft: '3px solid #043A5E',
    },
    timelineItemDestacar: {
        border: '2px solid #f1c40f',
        boxShadow: '0 0 10px rgba(241, 196, 15, 0.7)',
    },
    timelineDate: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
    },
    timelineDateText: {
        color: '#1ab394',
        marginLeft: '8px',
    },
    timelineContent: {
        paddingTop: '5px',
    },
    timelineContentP: {
        marginBottom: '5px',
    },
    timelineContentStrong: {
        color: '#333',
    }
};

const TimelineItem = ({ item, isAndamento = true }) => {
    const { t } = useTranslation();

    const data = isAndamento ? item.data_andamento : item.data_inicio;
    const descricao = item.descricao ? item.descricao.replace(/dl-horizontal/g, '') : t("projetodelei.noDescription");

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const [datePart] = dateString.split(" ");
        const [year, month, day] = datePart.split("-");
        return `${day}/${month}/${year}`;
    };

    const itemStyle = {
        ...styles.timelineItem,
        display: 'flex',
        alignItems: 'center',
    };

    const dateColumnStyle = {
        flex: '0 0 110px',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '20px',
        textAlign: 'center',
    };

    const contentColumnStyle = {
        flex: 1,
    };

    const iconLinkStyle = {
        display: 'block',
        marginBottom: '8px',
        fontSize: '1.6rem',
        color: '#333'
    };

    return (
        <div style={itemStyle}>
            <div style={dateColumnStyle}>
                {item.url && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={iconLinkStyle}>
                        <FaFilePdf />
                    </a>
                )}
                <span style={styles.timelineDateText}>{formatDate(data)}</span>
            </div>

            <div style={contentColumnStyle}>
                <p style={{ ...styles.timelineContentP, ...styles.timelineContentStrong }}><strong>{item.nome}</strong></p>
                {item.autor && (
                    <p style={styles.timelineContentP}>
                        <strong style={styles.timelineContentStrong}>{t("projetodelei.author")}: {item.autor}</strong>
                    </p>
                )}
                <p style={styles.timelineContentP} dangerouslySetInnerHTML={{ __html: descricao }} />
            </div>
        </div>
    );
};

function ProjetoDeLeiDetalhe() {
    const { t } = useTranslation();
    const { id } = useParams();

    const [plData, setPlData] = useState(null);
    const [andamentos, setAndamentos] = useState([]);
    const [comissoes, setComissoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [plResponse, andamentosResponse, comissoesResponse] = await Promise.all([
                    apiRequest("GET", `/projeto-de-lei/${id}`),
                    apiRequest("GET", `/projeto-de-lei-andamento`, "", { projeto_lei_id: id }),
                    apiRequest("GET", `/projeto-de-lei-comissao`, "", { projeto_lei_id: id })
                ]);
                setPlData(plResponse.data);
                setAndamentos(andamentosResponse.data || []);
                setComissoes(comissoesResponse.data || []);
            } catch (err) {
                setError(err.message || t("projetodelei.fetchError"));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, t]);

    const formatDate = (dateString) => {
        if (!dateString) return t("projetodelei.notInformed");
        const [datePart] = dateString.split(" ");
        const [year, month, day] = datePart.split("-");
        return `${day}/${month}/${year}`;
    };

    const renderLink = (url, text) => {
        const isValidUrl = url && (url.startsWith('http://') || url.startsWith('https://'));
        if (!isValidUrl) return <span>{text || t("projetodelei.notInformed")}</span>;

        return (
            <a href={url} target="_blank" rel="noopener noreferrer">
                {text} <FaExternalLinkAlt size={12} style={{ marginBottom: '2px' }} />
            </a>
        );
    };

    const exportPDF = async () => {
        setExporting(true);

        const input = document.getElementById("wrapper-content");

        try {
            const canvas = await html2canvas(input, { scale: 2 });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save("projeto-lei.pdf");
        } catch (err) {
            console.error("Erro ao exportar PDF:", err);
        } finally {
            setExporting(false);
        }
    };

    if (loading) return <Loading />;
    if (plData?.projetoDeLei.length === 0) return <div className="text-center p-4">{t("projetodelei.notFound")}</div>;
    if (error) return <div className="text-center p-4">Erro: {error}</div>;

    return (
        <div id="wrapper" style={{ backgroundColor: "#043A5E" }}>
            <SideMenu />

            <div id="page-wrapper" style={{ backgroundColor: "#FCFCFD" }}>
                <Navbar />

                <div className="wrapper wrapper-content">
                    <div className="mb-4">
                        <Link to="/projetodelei" className="btn btn-light">
                            <IoIosArrowBack size={20} /> {t("projetodelei.backToList")}
                        </Link>

                        <button
                            className="btn btn-success ml-2"
                            onClick={exportPDF}
                            disabled={exporting}
                        >
                            {exporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    {t("projetodelei.generatingPdf")}
                                </>
                            ) : (
                                <>
                                    <FaDownload style={{ marginRight: "5px" }} />
                                    {t("projetodelei.exportPdf")}
                                </>
                            )}
                        </button>
                    </div>

                    <div id="wrapper-content">
                        {plData && (
                            <div style={styles.cardProjetoLei}>
                                <h5 style={styles.cardTitle}>
                                    {renderLink(plData.projetoDeLei[0].url_pl, plData.projetoDeLei[0].nome)}
                                </h5>
                                <div className="row">
                                    <div className="col-md-6">
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.areaOfActivity")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].areaatuacao_nome || t("projetodelei.notInformed")}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.plUrl")}:</span> <span style={styles.infoValue}>{renderLink(plData.projetoDeLei[0].url_pl, plData.projetoDeLei[0].url_pl)}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.name")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].nome || t("projetodelei.notInformed")}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.type")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].tipo || t("projetodelei.notInformed")}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.billDate")}:</span> <span style={styles.infoValue}>{formatDate(plData.projetoDeLei[0].data_pl)}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.author")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].autor || t("projetodelei.notInformed")}</span></p>
                                    </div>
                                    <div className="col-md-6">
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.authorUrl")}:</span> <span style={styles.infoValue}>{renderLink(plData.projetoDeLei[0].url_autor, plData.projetoDeLei[0].url_autor)}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.document")}:</span> <span style={styles.infoValue}>{renderLink(plData.projetoDeLei[0].documento, plData.projetoDeLei[0].documento)}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.indexing")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].indexacao || t("projetodelei.notInformed")}</span></p>
                                        <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.processingRite")}:</span> <span style={styles.infoValue}>{plData.projetoDeLei[0].rito_tramitacao || t("projetodelei.notInformed")}</span></p>
                                    </div>
                                </div>
                                {plData.projetoDeLei[0].ementa && (
                                    <div className="row">
                                        <div className="col-md-12" style={styles.ementaContainer}>
                                            <p style={styles.paragraph}><span style={styles.infoLabel}>{t("projetodelei.summary")}:</span></p>
                                            <p style={styles.ementaText} dangerouslySetInnerHTML={{ __html: plData.projetoDeLei[0].ementa.replace(/\n/g, '<br />') }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="row mt-4">
                            <div className="col-md-6">
                                <h4 className="text-center mb-3 font-weight-bold" style={{ fontSize: '18px' }}>{t("projetodelei.proceedings")}</h4>
                                <div>
                                    {andamentos?.projetoDeLei.length > 0 ? (
                                        andamentos.projetoDeLei.map(andamento => <TimelineItem key={andamento.id} item={andamento} isAndamento={true} />)
                                    ) : (
                                        <p className="text-center">{t("projetodelei.noProceedings")}</p>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <h4 className="text-center mb-3 font-weight-bold" style={{ fontSize: '18px' }}>{t("projetodelei.commissions")}</h4>
                                <div>
                                    {comissoes?.projetoDeLei.length > 0 ? (
                                        comissoes.projetoDeLei.map(comissao => <TimelineItem key={comissao.id} item={comissao} isAndamento={false} />)
                                    ) : (
                                        <p className="text-center">{t("projetodelei.noCommissions")}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProjetoDeLeiDetalhe;